import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  Box,
  StepTitle,
  Circle,
  Button,
  Spinner,
  Text,
  Flex,
  StepSeparator,
  useToast,
  ButtonGroup,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Divider,
  useDisclosure,
  useColorModeValue,
  Card,
  CardBody,
} from "@chakra-ui/react";
import moment from "moment";
import { AiOutlineDatabase, AiOutlineIdcard } from "react-icons/ai";
import { BiGitMerge } from "react-icons/bi";
import { SubmitHandler, useForm, useFormState, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { startCase } from "lodash";
import ActionSideDrawer from "components/ActionSideDrawer";
import useActionSideDrawer from "components/ActionSideDrawer/hooks";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import { ActiveFilter } from "components/ControlBar/types";
import TabSelector, { TabOption } from "components/TabSelector/TabSelector";
import { CalendarEvent } from "components/Calendar/types";
import { useCurrentCompanyAdminQuery } from "features/Company/hooks";
import { useNewHireJourneyDetailQuery, useUpdateNewHireJourney } from "features/NewHireJourney/hooks";
import {
  ScheduleByStages,
  ScheduleWeekView,
  ScheduleMonthView,
  ScheduleListView,
} from "features/NewHireJourney/Components/Schedule";
import {
  actionToCalendarEvent,
  filterActionSummaryByText,
  filterByActionType,
  filterByTargetRelationType,
} from "features/Resources/Shared/helpers";
import { ActionSummary, ActionType, PrefixId } from "features/Resources/Shared/models";
import { RelationType } from "models/v2/onboardingJourneyNetworkRelation";
import FilterButton from "components/Button/FilterButton";
import TableFilterSelect from "components/Select/TableFilterSelect";
import { RELATION_TARET_TYPE } from "definitions/constants";
import { FloatingField } from "design-system";
import ExternalLink from "components/Link";
import { dirtyValues } from "helpers/form";
import { getLocalTimezone } from "helpers/time";
import { DEFAULT_TIMEZONE } from "definitions/constants";
import { useLocation, useNavigate, useParams } from "react-router";
import { useFormDetailQuery } from "features/Form/hooks";
import { BulkScheduleContent, BulkScheduleFooterState, usePrepareBulkSchedule } from "features/BulkSchedule";
import { useCurrentUserQuery } from "features/User/hooks";
import { useSharedResourcesListByNewHireIdQuery } from "features/Resources/Shared/hooks";
import { useFeature } from "hooks/useFeature";
import AddPathsForm from "../Form/AddPathsForm";
import autoFormDefaultValues, { buildFormSchema } from "../Form/buildForm";
import InfoAlert from "../Form/NewHireAlert";
import ReviewModal from "../Form/ReviewModal";
import useKickOffOnboarding from "../Form/useKickOffOnboarding";
import PendingNewHireForm from "../Form/NewHirePendingForm";
import { NewHireFormValues, pendingNewHireSchema, ONBOARDING_PLAN_URL_FIELD } from "../Form/definition";
import { applyExtendedRoleVisibility, getPendingFormSections } from "../Form/pendingNewHireForm";

interface AddInfoSectionProps {
  newHireJourneyId: string;
  form: ReturnType<typeof useForm>;
  isLoaded: boolean;
}

// Step components
function AddInfoSection({ form, newHireJourneyId, isLoaded }: AddInfoSectionProps) {
  return (
    <Flex
      width="100%"
      maxWidth="900px"
      justify="center"
      mb="2"
      mt="2" // the pending/auto form has margin of 4, so this aligns this section with everything else
    >
      <PendingNewHireForm form={form} isLoaded={isLoaded} newHireJourneyId={newHireJourneyId} />
    </Flex>
  );
}

function BuildOnboardingSection({
  newHireJourneyId,
  form: mainForm,
}: {
  newHireJourneyId: string;
  form: ReturnType<typeof useForm>;
}) {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const { data: company } = useCurrentCompanyAdminQuery();
  const { data: newHireJourney } = useNewHireJourneyDetailQuery(newHireJourneyId || "");

  const { isValid } = useFormState({ control: mainForm.control });
  const watchedValues = useWatch({ control: mainForm.control });
  const { mutate } = useUpdateNewHireJourney(newHireJourney?.id || "");

  const form = useForm<any>({
    mode: "onBlur",
    resolver: yupResolver(pendingNewHireSchema.pick([ONBOARDING_PLAN_URL_FIELD.name as "onboardingPlanUrl"])),
    values: {
      onboardingPlanUrl: newHireJourney?.onboardingPlanUrl,
    },
  });
  const {
    control: innerControl,
    formState: { errors, dirtyFields },
    handleSubmit,
  } = form;

  const onSubmit: SubmitHandler<NewHireFormValues> = (data) => {
    const filteredData = dirtyValues(data, dirtyFields);
    if (Object.keys(filteredData).length > 0) mutate(filteredData);
  };

  useEffect(() => {
    forceUpdate();
  }, [isValid, watchedValues]);

  return (
    <Box width="100%" maxWidth="900px" height="100%">
      <Box textAlign="center" mt="4" mb="4">
        {isValid ? (
          <Text color="fg.emphasized">🎉 You’ve completed the profile information! 🎉</Text>
        ) : (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle color="fg.subtle">Form Error!</AlertTitle>
            <AlertDescription color="fg.subtle">Please go back to the first step and fix the errors.</AlertDescription>
          </Alert>
        )}
      </Box>
      {!!company && company.hasOnboardingPlans ? (
        <>
          <Card>
            <CardBody>
              <Flex direction="column" mt="6" mb="1">
                <Text fontSize="lg" fontWeight="bold" color="fg.emphasized" my="2">
                  Add Onboarding Plan Template
                </Text>
                <Text color="fg.subtle" fontSize="sm">
                  Add the URL to the onboarding plan for the new hire to be filled out by the manager.
                </Text>
                {company?.onboardingPlanTemplatesUrl ? (
                  <Text color="fg.subtle" fontSize="sm">
                    You can create it from{" "}
                    <ExternalLink
                      as="span"
                      to={company.onboardingPlanTemplatesUrl}
                      text="this onboarding plan template"
                    />
                    .
                  </Text>
                ) : null}
                <Box mt="4" onBlur={handleSubmit(onSubmit)}>
                  <FloatingField
                    name="onboardingPlanUrl"
                    errors={errors.onboardingPlanUrl?.message as string | undefined}
                  >
                    <FloatingField.Label>Onboarding Plan Url</FloatingField.Label>
                    <FloatingField.Text control={innerControl} name="onboardingPlanUrl" />
                  </FloatingField>
                </Box>
              </Flex>
            </CardBody>
          </Card>
          <Divider my="8" />
        </>
      ) : null}
      <Card>
        <CardBody>
          <Flex direction="column" mb="1">
            <Text fontSize="lg" fontWeight="bold" color="fg.emphasized" my="2">
              Paths
            </Text>
            <AddPathsForm newHireJourneyId={newHireJourneyId} />
          </Flex>
        </CardBody>
      </Card>
    </Box>
  );
}

function AddMeetings({
  newHireJourneyId,
  onFooterStateChange,
  onComplete,
}: {
  newHireJourneyId: string;
  onFooterStateChange?: (state: BulkScheduleFooterState) => void;
  onComplete?: () => void;
}) {
  const { data: currentUser } = useCurrentUserQuery();
  const timezone = currentUser?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Box width="100%" display="flex" flexDirection="column" alignItems="flex-start" mb="2" mt="6">
      <Card width="100%">
        <CardBody>
          <Flex direction="column" mb="1">
            <Text fontSize="lg" fontWeight="bold" color="fg.emphasized" my="2">
              Meetings
            </Text>
            <BulkScheduleContent
              journeyId={newHireJourneyId}
              timezone={timezone}
              onFooterStateChange={onFooterStateChange}
              onComplete={onComplete}
            />
          </Flex>
        </CardBody>
      </Card>
    </Box>
  );
}

type ReviewViewType = "week" | "month" | "list" | "stages";

const stakeholderOptions = [
  { label: "New Hire", value: RELATION_TARET_TYPE.NEW_HIRE },
  { label: "Manager", value: RELATION_TARET_TYPE.MANAGER },
  { label: "People Team", value: RELATION_TARET_TYPE.PEOPLE_TEAM },
  { label: "Buddy", value: RELATION_TARET_TYPE.BUDDY },
];

const actionTypeOptions = [
  { label: "Email", value: "email" },
  { label: "Meeting", value: "meeting" },
  { label: "Message", value: "message" },
  { label: "Task", value: "task" },
];

function ReviewOnboardingSection({ newHireJourneyId }: { newHireJourneyId: string }) {
  const { data: actions = [] } = useSharedResourcesListByNewHireIdQuery(newHireJourneyId);
  const { data: newHireJourney } = useNewHireJourneyDetailQuery(newHireJourneyId);
  const { isOpen, onClose, selectedActionPrefixId, expandClickHandlerByPrefixId } = useActionSideDrawer();

  const [currentView, setCurrentView] = useState<ReviewViewType>("week");
  const [showWeekends, setShowWeekends] = useState(false);

  // Default to the week of the earliest action so users don't see empty weeks
  const earliestActionDate = useMemo(() => {
    if (actions.length === 0) return moment();
    const dates = actions.map((a) => moment(a.unifiedDate)).filter((d) => d.isValid());
    return dates.length > 0 ? moment.min(dates) : moment();
  }, [actions]);

  const [selectedDate, setSelectedDate] = useState(moment());
  const initialDateSet = useRef(false);

  useEffect(() => {
    if (actions.length > 0 && !initialDateSet.current) {
      initialDateSet.current = true;
      setSelectedDate(earliestActionDate);
    }
  }, [actions, earliestActionDate]);

  // Filter state
  const [searchText, setSearchText] = useState("");
  const [selectedStakeholder, setSelectedStakeholder] = useState<{ label: string; value: string } | null>(null);
  const [selectedActionType, setSelectedActionType] = useState<{ label: string; value: string } | null>(null);
  const [hasIssues, setHasIssues] = useState(false);

  const timezone = newHireJourney?.newHire?.timezone || getLocalTimezone();

  // Filter actions
  const filteredActions = useMemo(() => {
    let result = filterActionSummaryByText(actions, searchText);
    result = filterByActionType(result, (selectedActionType?.value as ActionType) || "");
    result = filterByTargetRelationType(result, (selectedStakeholder?.value as RelationType) || "");
    if (hasIssues) {
      result = result.filter((action) => action.action?.issueIds && action.action.issueIds.length > 0);
    }
    return result;
  }, [actions, searchText, selectedActionType, selectedStakeholder, hasIssues]);

  // Convert filtered actions to calendar events
  const events = useMemo(
    () => filteredActions.map((action) => actionToCalendarEvent(action, timezone ?? DEFAULT_TIMEZONE)),
    [filteredActions, timezone],
  );

  const hasActiveFilters = selectedStakeholder !== null || selectedActionType !== null || hasIssues;

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const filters: ActiveFilter[] = [];
    if (selectedStakeholder) {
      filters.push({
        key: "stakeholder",
        label: `Stakeholder: ${selectedStakeholder.label}`,
        onRemove: () => setSelectedStakeholder(null),
      });
    }
    if (selectedActionType) {
      filters.push({
        key: "type",
        label: `Type: ${selectedActionType.label}`,
        onRemove: () => setSelectedActionType(null),
      });
    }
    if (hasIssues) {
      filters.push({ key: "has_issues", label: "With Issues", onRemove: () => setHasIssues(false) });
    }
    return filters;
  }, [selectedStakeholder, selectedActionType, hasIssues]);

  const handleReset = useCallback(() => {
    setSearchText("");
    setSelectedStakeholder(null);
    setSelectedActionType(null);
    setHasIssues(false);
  }, []);

  const handleEventClick = useCallback(
    (event: CalendarEvent) => {
      expandClickHandlerByPrefixId(event.id as PrefixId);
    },
    [expandClickHandlerByPrefixId],
  );

  const handleActionClick = useCallback(
    (action: ActionSummary) => {
      expandClickHandlerByPrefixId(action.prefixId as PrefixId);
    },
    [expandClickHandlerByPrefixId],
  );

  return (
    <Box width="100%" display="flex" flexDirection="column" alignItems="flex-start" mb="2" mt="6">
      <Flex maxWidth="100%" width="100%" direction="column" gap="4">
        <FilterSortSearchBar
          searchPlaceholder="Search actions..."
          searchValue={searchText}
          onSearchChange={setSearchText}
          currentSort=""
          defaultSort=""
          onSortChange={() => {}}
          sortOptions={[]}
          hideSort
          quickFilters={
            <FilterButton onClick={() => setHasIssues(!hasIssues)} isActive={hasIssues} size="sm">
              With Issues
            </FilterButton>
          }
          allFilters={
            <>
              <FilterButton onClick={() => setHasIssues(!hasIssues)} isActive={hasIssues}>
                With Issues
              </FilterButton>
              <TableFilterSelect
                placeholder="Filter by Stakeholder"
                value={selectedStakeholder}
                onChange={setSelectedStakeholder}
                defaultOptions={stakeholderOptions}
                minWidth="180px"
                isClearable={false}
              />
              <TableFilterSelect
                placeholder="Filter by Type"
                value={selectedActionType}
                onChange={setSelectedActionType}
                defaultOptions={actionTypeOptions}
                minWidth="140px"
                isClearable={false}
              />
            </>
          }
          activeFilters={activeFilters}
          onReset={handleReset}
          hasActiveFilters={hasActiveFilters}
        />

        <Flex justifyContent="space-between" alignItems="center">
          <Box>
            <InfoAlert />
          </Box>
          <TabSelector value={currentView} onChange={setCurrentView} size="sm">
            <TabOption value="week">Week</TabOption>
            <TabOption value="month">Month</TabOption>
            <TabOption value="list">List</TabOption>
            <TabOption value="stages">Stages</TabOption>
          </TabSelector>
        </Flex>

        {currentView === "week" && (
          <ScheduleWeekView
            events={events}
            selectedDate={selectedDate}
            timezone={timezone}
            showWeekends={showWeekends}
            onDateChange={setSelectedDate}
            onShowWeekendsChange={setShowWeekends}
            onEventClick={handleEventClick}
            onEventUpdate={() => {}}
          />
        )}

        {currentView === "month" && (
          <ScheduleMonthView
            events={events}
            selectedDate={selectedDate}
            timezone={timezone}
            showWeekends={showWeekends}
            onDateChange={setSelectedDate}
            onShowWeekendsChange={setShowWeekends}
            onEventClick={handleEventClick}
          />
        )}

        {currentView === "list" && (
          <ScheduleListView
            actions={filteredActions}
            startDate={newHireJourney?.startDate}
            timezone={timezone}
            onActionClick={handleActionClick}
          />
        )}

        {currentView === "stages" && (
          <ScheduleByStages
            actions={filteredActions}
            newHireStartDate={newHireJourney?.startDate}
            isPending
            onActionClick={handleActionClick}
            showFilters={false}
          />
        )}
      </Flex>

      <ActionSideDrawer isOpen={isOpen} onClose={onClose} prefixId={selectedActionPrefixId} />
    </Box>
  );
}

function fieldNamesToTitleize(fieldNames: string[]) {
  return fieldNames
    .filter((fieldName) => !["country", "googleLocationId", "rawLocation"].includes(fieldName))
    .map((fieldName) => startCase(fieldName))
    .join(", ");
}

const steps = [
  { title: "Profile Info", icon: AiOutlineIdcard },
  { title: "Onboarding Plan", icon: BiGitMerge },
  { title: "Meetings", icon: BiGitMerge },
  { title: "Review", icon: AiOutlineDatabase },
];

function KickOff({ newHireJourneyId }: { newHireJourneyId: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { step } = useParams();

  const stepSlugs = ["info", "onboarding-plan", "meetings", "review"];

  const basePath = location.pathname.split("/").slice(0, -1).join("/"); // removes the last segment

  const activeStep = useMemo(() => {
    const index = stepSlugs.findIndex((s) => s === step);
    return index !== -1 ? index : basePath.endsWith("/pending") ? 0 : -1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (activeStep === -1) {
      navigate("/not-found", { replace: true });
    }
  }, [activeStep, navigate]);

  const { isOpen: reviewModalIsOpen, onOpen: openReviewModal, onClose: closeReviewModal } = useDisclosure();

  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast({
    duration: 5000,
  });
  const { data: newHireJourney, isLoading: isNewHireJourneyLoading } = useNewHireJourneyDetailQuery(newHireJourneyId);

  const { data: company } = useCurrentCompanyAdminQuery();
  const { data: pendingNewHireForm } = useFormDetailQuery(company?.pendingNewHireFormId as string, {
    enabled: !!company?.pendingNewHireFormId,
  });
  const { enabled: extendedRolesEnabled } = useFeature("extended_journey_roles");
  const defaultSections = useMemo(() => getPendingFormSections({ extendedRolesEnabled }), [extendedRolesEnabled]);
  const customSections = useMemo(
    () => applyExtendedRoleVisibility(pendingNewHireForm?.schema?.sections || [], extendedRolesEnabled),
    [pendingNewHireForm?.schema?.sections, extendedRolesEnabled],
  );
  const activeSections = company && !company.pendingNewHireFormId ? defaultSections : customSections;

  const schema = useMemo(() => buildFormSchema(activeSections), [activeSections]);

  const form = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
    values: autoFormDefaultValues(activeSections, newHireJourney),
    resetOptions: {
      keepDirtyValues: true,
      keepErrors: true,
      keepTouched: true,
    },
  });

  const [firstName, googleLocationId] = useWatch({
    control: form.control,
    name: ["firstName", "googleLocationId"],
  });

  const { mutate: prepareBulkSchedule } = usePrepareBulkSchedule(newHireJourneyId);
  const [meetingFooterState, setMeetingFooterState] = useState<BulkScheduleFooterState | null>(null);

  // Pre-calculate meeting reservations as soon as the flow loads so they are ready
  // by the time the user reaches the meetings step.
  useEffect(() => {
    if (newHireJourneyId) prepareBulkSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newHireJourneyId]);

  useEffect(() => {
    if (activeStep !== 2) {
      setMeetingFooterState(null); // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [activeStep]);

  const hasLoaded = useRef<boolean>(false);
  const prevGoogleLocationId = useRef<string | null>(null);

  useEffect(() => {
    if (firstName && hasLoaded.current === false) {
      hasLoaded.current = true;
      prevGoogleLocationId.current = googleLocationId;
    }
  }, [firstName, googleLocationId]);

  const renderStepContent = (innerStep: number) => {
    switch (innerStep) {
      case 0:
        return <AddInfoSection isLoaded={!isNewHireJourneyLoading} form={form} newHireJourneyId={newHireJourneyId} />;
      case 1:
        return <BuildOnboardingSection form={form} newHireJourneyId={newHireJourneyId} />;
      case 2:
        return (
          <AddMeetings
            newHireJourneyId={newHireJourneyId}
            onFooterStateChange={setMeetingFooterState}
            onComplete={handleSubmitAndAdvance}
          />
        );
      case 3:
        return <ReviewOnboardingSection newHireJourneyId={newHireJourneyId} />;
      default:
        return null;
    }
  };

  const handleStepClick = (index: number) => {
    navigate(`${basePath}/${stepSlugs[index]}`);
  };

  const handleSubmitAndAdvance = useCallback(async () => {
    setIsLoading(true);
    try {
      if (activeStep === 0) {
        const isValid = await form.trigger();
        if (!isValid) {
          const errorFieldNames = fieldNamesToTitleize(Object.keys(form.formState.errors));
          toast({
            title: "Form Error",
            description: `There are errors in the following fields: ${errorFieldNames}`,
            status: "error",
          });
          return;
        }
      }

      const nextStep = activeStep + 1;
      if (nextStep < stepSlugs.length) {
        navigate(`${basePath}/${stepSlugs[nextStep]}`);
      }
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, form, toast, navigate, basePath]);

  const { handleSubmit, isSubmitting } = useKickOffOnboarding({ newHireJourneyId, onClose: closeReviewModal });
  const headerBg = useColorModeValue("creamBg.opacity10", "gray.500");
  const footerBg = useColorModeValue("white", "gray.800");

  return (
    <Flex width="100%" flex="1" minHeight={0} direction="column" align="center" overflow="hidden">
      <Flex
        id="stepperHeader"
        flexShrink={0}
        justify="center"
        zIndex="3"
        bg={headerBg}
        width="calc(100% + 48px)"
        px="8"
        py="4"
      >
        <Box width="100%" minWidth="220px" alignSelf="center">
          <Stepper index={activeStep} gap="3">
            {steps.map((stepItem, index) => (
              <Step key={stepItem.title} onClick={() => handleStepClick(index)} cursor="pointer">
                <StepIndicator
                  bg="transparent"
                  borderWidth="0 !important"
                  boxShadow="none !important"
                  sx={{
                    "[data-status=complete] &": {
                      background: "transparent",
                    },
                    "[data-status=active] &": {
                      background: "transparent",
                    },
                    "[data-status=incomplete] &": {
                      background: "transparent",
                    },
                  }}
                >
                  <StepStatus
                    complete={
                      <Circle size="8" bg="transparent" borderWidth="2px" borderColor="successGreen.500">
                        <Circle size="6" bg="successGreen.500" borderColor="transparent" />
                      </Circle>
                    }
                    incomplete={<Circle size="6" bg="gray.400" borderColor="transparent" />}
                    active={
                      <Circle size="8" bg="transparent" borderWidth="2px" borderColor="brand.500">
                        <Circle size="6" bg="brand.500" borderColor="transparent" />
                      </Circle>
                    }
                  />
                </StepIndicator>
                <StepTitle color="fg.emphasized">{stepItem.title}</StepTitle>
                <StepSeparator
                  _horizontal={{
                    "[data-status=complete] &": {
                      background: "successGreen.500",
                    },
                  }}
                />
              </Step>
            ))}
          </Stepper>
        </Box>
      </Flex>

      <Flex flex="1" minHeight={0} width="100%" justify="center" overflowY="auto">
        {renderStepContent(activeStep)}
      </Flex>

      <Flex
        flexShrink={0}
        zIndex="10"
        backgroundColor={footerBg}
        padding="10px"
        justifyContent="space-around"
        alignItems="center"
        width="calc(100% + 48px)"
        paddingRight="20"
        borderTop="1px solid var(--chakra-colors-cdsIndigo-500)"
        sx={{
          _after: {
            content: '""',
            position: "absolute",
            bottom: "-16px",
            left: 0,
            right: 0,
            height: "16px",
            bg: footerBg,
          },
        }}
      >
        <Box />

        <ButtonGroup gap="8">
          {activeStep !== 0 ? (
            <Button isDisabled={isLoading} variant="link" onClick={() => handleStepClick(activeStep - 1)}>
              Back
            </Button>
          ) : (
            <Box as="span" visibility="hidden">
              Back
            </Box>
          )}

          {activeStep === 2 && meetingFooterState ? (
            meetingFooterState.isLoading || meetingFooterState.isConfirming ? (
              <Button
                isDisabled
                isLoading
                loadingText={meetingFooterState.isLoading ? "Finding meeting times..." : "Scheduling meetings..."}
              >
                {meetingFooterState.isLoading ? "Finding meeting times..." : "Scheduling meetings..."}
              </Button>
            ) : meetingFooterState.hasResults && meetingFooterState.failedCount > 0 && meetingFooterState.onRetry ? (
              <Button colorScheme="blue" onClick={meetingFooterState.onRetry}>
                Retry {meetingFooterState.failedCount} Failed Meeting
                {meetingFooterState.failedCount !== 1 ? "s" : ""}
              </Button>
            ) : meetingFooterState.hasResults ? (
              <Button onClick={handleSubmitAndAdvance}>Next</Button>
            ) : (
              <Button onClick={meetingFooterState.onSchedule} isDisabled={meetingFooterState.selectedCount === 0}>
                Schedule {meetingFooterState.selectedCount} Meeting{meetingFooterState.selectedCount !== 1 ? "s" : ""}
              </Button>
            )
          ) : (
            <Button
              isDisabled={activeStep === steps.length - 1 ? false : isLoading}
              onClick={activeStep === steps.length - 1 ? openReviewModal : handleSubmitAndAdvance}
              isLoading={isLoading}
              loadingText="Submitting"
            >
              {isLoading ? <Spinner size="sm" /> : activeStep === steps.length - 1 ? "Kick Off Onboarding" : "Next"}
            </Button>
          )}
        </ButtonGroup>
      </Flex>
      <ReviewModal
        newHireJourneyId={newHireJourneyId}
        isOpen={reviewModalIsOpen}
        onClose={closeReviewModal}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
      />
    </Flex>
  );
}

export default KickOff;
